"use client";

import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Sparkles, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmartUploadDialog } from "@/components/chatbot/concept-visual-studio/_components/SmartUploadDialog";
import { CreateProductModal } from "@/components/chatbot/concept-visual-studio/_components/CreateProductModal";
import { MediaUploadDropzoneArea } from "./MediaUploadDropzoneArea";

interface ProductUploadDropzoneProps {
  brandId: string;
  onSaved?: () => void;
}

export function ProductUploadDropzone({
  brandId,
  onSaved,
}: ProductUploadDropzoneProps) {
  const [smartUploadOpen, setSmartUploadOpen] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setDroppedFiles(acceptedFiles);
      setSmartUploadOpen(true);
    }
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    noClick: true,
  });

  const handleSmartUploadClose = (open: boolean) => {
    if (!open) setDroppedFiles([]);
    setSmartUploadOpen(open);
  };

  return (
    <>
      <div className="mb-6">
        <MediaUploadDropzoneArea
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          isDragAccept={isDragAccept}
          isDragReject={isDragReject}
          isUploading={false}
        >
          <div className="flex gap-x-3">
            <Button
              variant="outline"
              className="mb-2 border-[#636AE8] text-[#636AE8] hover:bg-[#636AE8] hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                setSmartUploadOpen(true);
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Smart Upload
            </Button>

            <Button
              variant="outline"
              className="mb-2 border-[#636AE8] text-[#636AE8] hover:bg-[#636AE8] hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                setCreateProductOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create Product SKU
            </Button>
          </div>

          <p className="text-sm text-gray-500 mt-1">
            Drop product images here to smart upload, or use the buttons above
          </p>
        </MediaUploadDropzoneArea>
      </div>

      <SmartUploadDialog
        open={smartUploadOpen}
        onOpenChange={handleSmartUploadClose}
        brandId={brandId}
        initialFiles={droppedFiles}
        onSaved={() => {
          setDroppedFiles([]);
          onSaved?.();
        }}
      />

      <CreateProductModal
        open={createProductOpen}
        onOpenChange={setCreateProductOpen}
        brandId={brandId}
      />
    </>
  );
}
